import React from 'react';
import { Button } from '@/components/ui/button';
import { Cloud, Lightbulb } from 'lucide-react';

/**
 * Reusable Document Upload Section Component
 * 
 * @param {Object} props
 * @param {string} props.title - Section title (e.g., "Upload Documents")
 * @param {string} props.description - Description text for the upload section
 * @param {Array} props.uploadedFiles - Array of uploaded file objects
 * @param {boolean} props.isUploading - Whether upload is in progress
 * @param {string} props.uploadProgress - Progress message text
 * @param {string} props.uploadStatus - Status message text
 * @param {Function} props.onUpload - Handler for file upload (receives FileList)
 * @param {Function} props.onViewFile - Handler to view a file (receives file object)
 * @param {Function} props.onDeleteFile - Handler to delete a file (receives fileId)
 * @param {boolean} props.isViewingFile - Whether a file is being viewed
 * @param {boolean} props.disabled - Whether upload is disabled
 * @param {boolean} props.hideUploadArea - Whether to hide the upload area (only show uploaded files)
 * @param {string} props.uploadInputId - Unique ID for the file input (default: "document-upload")
 * @param {string} props.acceptedFormats - Accepted file formats (default includes PDF, Word, Excel, RTF, ODT, TXT, CSV, images)
 * @param {string} props.maxSizeText - Max file size text (default: "Max 10MB each")
 */
const DocumentUploadSection = ({
  title = "Upload Documents",
  description = "Upload your documents for review. Supported formats: PDF, DOC, DOCX, RTF, XLS, XLSX, ODT, ODS, TXT, CSV, JPG, PNG (Max 10MB each)",
  uploadedFiles = [],
  isUploading = false,
  uploadProgress = '',
  uploadStatus = '',
  onUpload,
  onViewFile,
  onDeleteFile,
  isViewingFile = false,
  disabled = false,
  hideUploadArea = false,
  uploadInputId = "document-upload",
  acceptedFormats = ".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png",
  maxSizeText = "Max 10MB each"
}) => {
  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0 && onUpload) {
      onUpload(files);
      // Reset input value to allow re-uploading the same file
      event.target.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      {!hideUploadArea && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      
      {/* Upload Drop Zone */}
      {!hideUploadArea && (
      <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center">
        <div className="flex justify-center mb-3">
          <Cloud className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {description}
        </p>
        <p className="text-xs text-emerald-600 mb-4 flex items-center justify-center gap-1">
          <Lightbulb className="w-3 h-3" />
          Documents are stored securely in trusted cloud storage with encryption and access controls.
        </p>
        
        {/* Upload Progress Indicator */}
        {isUploading && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm font-medium text-blue-800">{uploadProgress}</span>
            </div>
            {uploadStatus && (
              <p className="text-xs text-blue-600">{uploadStatus}</p>
            )}
          </div>
        )}
        
        <input
          type="file"
          multiple
          accept={acceptedFormats}
          onChange={handleFileChange}
          disabled={isUploading || disabled}
          className="hidden"
          id={uploadInputId}
        />
        <label
          htmlFor={uploadInputId}
          className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
            isUploading || disabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Uploading to Secure Storage...
            </>
          ) : (
            'Upload Documents'
          )}
        </label>
      </div>
      )}

      {/* Uploaded Files Section */}
      {uploadedFiles.length > 0 && (
        <div className={hideUploadArea ? "" : "mt-6"}>
          <h4 className={hideUploadArea ? "text-lg font-semibold mb-4" : "text-md font-semibold mb-4"}>
            Uploaded Documents ({uploadedFiles.length})
          </h4>
          <div className="space-y-3">
            {uploadedFiles.map((file) => (
              <div key={file.id || file.s3Key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Cloud className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Unknown date'} • {formatFileSize(file.size || 0)}
                      <span className="ml-2 text-emerald-600">• Secure Storage</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {onViewFile && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewFile(file)}
                      disabled={isViewingFile}
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isViewingFile ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        'View Document'
                      )}
                    </Button>
                  )}
                  {onDeleteFile && !disabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDeleteFile(file.id || file.s3Key)}
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadSection;

